import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../Auth";

import { signOut, sendPasswordResetEmail } from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db, auth } from "../../base";

import { AiOutlineLeft as BackIcon } from "react-icons/ai";

import { spellOutDate } from "../../utils/formatDate";
import { capitalize } from "../../utils/capitalize";
import {
  inviteMember,
  revokeMember,
  reassignMember,
  sendInviteEmail,
  claimOwnerSeat,
} from "../../functions/accessApi";
import TierPill from "./TierPill";
import StatusBadge from "./StatusBadge";
import { TIERS, DEFAULT_TIER } from "../../data/tierAccess";

// Resolve a grant's renewal/expiry date: invite grants surface the Stripe
// renewal (currentPeriodEnd); admin grants use expiresAt. Returns the date and
// whether it is already past.
function grantRenewal(grant) {
  const source = grant.currentPeriodEnd || grant.expiresAt;
  const date = source?.seconds ? new Date(source.seconds * 1000) : null;
  return { date, isExpired: date && date < new Date() };
}

// Display status for a managed seat, in the admin table's vocabulary: past its
// renewal date -> "expired"; otherwise claimed -> "active", else "unclaimed".
function seatStatus(grant, isExpired) {
  if (isExpired) {
    return "expired";
  }
  return grant.memberUid ? "active" : "unclaimed";
}

// One row of the "Seats I Manage" table: an assigned (non-owner) grant with
// claim status and the manage actions. Send-invite-email, Reassign, and Revoke
// are available only while the seat is unclaimed; once a member has signed in
// and claimed the seat the owner can no longer reassign or revoke it.
function SeatRow({ grant, busy, onSendEmail, onReassign, onRevoke }) {
  const claimed = !!grant.memberUid;
  const { date: renewalDate, isExpired } = grantRenewal(grant);
  return (
    <tr>
      <td className="ellipsisCell" title={grant.email}>
        {grant.email}
      </td>
      <td>
        <StatusBadge status={seatStatus(grant, isExpired)} />
      </td>
      <td>
        <TierPill tier={grant.tier} />
      </td>
      <td style={{ color: isExpired ? "red" : "inherit" }}>
        {renewalDate ? spellOutDate(renewalDate) : "—"}
      </td>
      <td>
        <div className="licenseRowActions">
          {!claimed && (
            <button
              className="rowButton"
              disabled={busy}
              onClick={() => onSendEmail(grant)}
            >
              Send invite email
            </button>
          )}
          {!claimed && (
            <button
              className="rowButton"
              disabled={busy}
              onClick={() => onReassign(grant)}
            >
              Reassign
            </button>
          )}
          {!claimed && (
            <button
              className="rowButton rowButtonDanger"
              disabled={busy}
              onClick={() => onRevoke(grant)}
            >
              Revoke
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// Read-only row showing the caller's own consumed seat under "My Access".
// myAccessGrant is always an access/{email} seat (an invite, an admin grant, or
// the owner's own consumed seat), so its `tier` IS the access tier — buying a
// higher tier to delegate (tracked separately in owners/) never touches it.
function MyAccessRow({ grant }) {
  const { date: renewalDate, isExpired } = grantRenewal(grant);
  return (
    <tr>
      <td>
        <TierPill tier={grant.tier} />
      </td>
      <td style={{ color: isExpired ? "red" : "inherit" }}>
        {renewalDate ? spellOutDate(renewalDate) : "—"}
      </td>
      <td className="ellipsisCell" title={grant.providerEmail || ""}>
        {grant.providerEmail || "—"}
      </td>
    </tr>
  );
}

// Unified account view for every non-admin user. Profile editing is always
// present; below it two OPTIONAL sections backed by the email-keyed access
// grants:
//   - "My Access"      — the caller's claimed seat (licenseData.myAccessGrant)
//   - "Seats I Manage" — the owner grant + assigned seats (managedGrants)
export function UserAccount(props) {
  const { currentUser, authUserData, setAuthUserData } =
    useContext(AuthContext);
  const navigate = useNavigate();

  // Hold the grant data in local state so invite/revoke/reassign can refresh it
  // without a full page reload.
  const [managedGrants, setManagedGrants] = useState(
    props.licenseData?.managedGrants || [],
  );
  const [myAccessGrant, setMyAccessGrant] = useState(
    props.licenseData?.myAccessGrant || null,
  );

  const ownerGrant = managedGrants.find((g) => g.isOwner) || null;
  const assignedSeats = managedGrants.filter((g) => !g.isOwner);

  // Sparse, lazily-created docs only carry {userID, email, userType}; default
  // every field to "" so the inputs stay controlled (no React warning).
  const [firstName, setFirstName] = useState(props.userData.firstName ?? "");
  const [lastName, setLastName] = useState(props.userData.lastName ?? "");
  const [email] = useState(props.userData.email ?? "");
  const [practice, setPractice] = useState(props.userData.practice ?? "");
  const [street, setStreet] = useState(props.userData.street ?? "");
  const [city, setCity] = useState(props.userData.city ?? "");
  const [country, setCountry] = useState(props.userData.country ?? "");
  const [zipCode, setZipCode] = useState(props.userData.zipCode ?? "");

  // Invite form state. Seats are accounted PER TIER, so the owner picks which
  // tier to assign when they hold seats of more than one tier.
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteTier, setInviteTier] = useState("");
  const [ownSeatTier, setOwnSeatTier] = useState("");
  // Per-action busy flag (disables the whole seat table during a write).
  const [busy, setBusy] = useState(false);

  // Per-tier seat accounting. `seatsByTier` is the derived capacity per tier on
  // the owner record. Every seat the owner backs — invitees AND the owner's own
  // consumed seat — is a row in `assignedSeats` (access where providerUid==uid),
  // so a simple per-tier count is the full used count (no owner special case).
  const seatsByTier = ownerGrant?.seatsByTier || {};
  const usedForTier = (t) =>
    assignedSeats.filter((g) => (g.tier || DEFAULT_TIER) === t).length;
  const freeForTier = (t) => (seatsByTier[t] || 0) - usedForTier(t);
  // Tiers to display: those with capacity, plus any tier a seat is already
  // assigned at (so over-allocation after a partial cancel still shows up).
  const displayTiers = TIERS.filter(
    (t) =>
      (seatsByTier[t] || 0) > 0 ||
      assignedSeats.some((g) => (g.tier || DEFAULT_TIER) === t),
  );
  const tiersWithFree = displayTiers.filter((t) => freeForTier(t) > 0);
  const totalSeats = displayTiers.reduce((n, t) => n + (seatsByTier[t] || 0), 0);
  const totalUsed = displayTiers.reduce((n, t) => n + usedForTier(t), 0);
  const freeSeats = totalSeats - totalUsed;
  // Tier selections fall back to the first tier that still has a free seat.
  const effInviteTier = inviteTier || tiersWithFree[0] || "";
  const effOwnSeatTier = ownSeatTier || tiersWithFree[0] || "";

  // Re-query the owner record + seats after a mutation. Ownership lives in
  // owners/{email}; the seats it backs (and the caller's own consumed seat) in
  // access/{email}.
  async function reloadGrants() {
    const uid = currentUser.uid;
    const [ownerSnap, seatsSnap, mineSnap] = await Promise.all([
      getDocs(query(collection(db, "owners"), where("providerUid", "==", uid))),
      getDocs(query(collection(db, "access"), where("providerUid", "==", uid))),
      getDocs(query(collection(db, "access"), where("memberUid", "==", uid))),
    ]);
    const ownerGrant = ownerSnap.empty
      ? null
      : { id: ownerSnap.docs[0].id, ...ownerSnap.docs[0].data(), isOwner: true };
    const seats = seatsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setManagedGrants(ownerGrant ? [ownerGrant, ...seats] : seats);
    setMyAccessGrant(
      mineSnap.empty ? null : { id: mineSnap.docs[0].id, ...mineSnap.docs[0].data() },
    );
  }

  const handleEditInformation = async (e) => {
    e.preventDefault();
    props.setLoading(true);
    const userDocRef = doc(db, "users", props.userData.userID);
    await setDoc(
      userDocRef,
      { firstName, lastName, practice, street, city, country, zipCode },
      { merge: true },
    );
    const userDocSnap = await getDoc(userDocRef);
    props.setUserData(userDocSnap.data());
    props.setLoading(false);
  };

  // Send the signed-in user a password-reset email. Google-sign-in accounts
  // have no password, so surface a manage-in-Google message instead (gotcha
  // #16).
  const handleResetSelf = async () => {
    if (currentUser?.providerData?.[0]?.providerId === "google.com") {
      alert(
        "Your account uses Google sign-in — manage your password in your " +
          "Google account.",
      );
      return;
    }
    try {
      await sendPasswordResetEmail(auth, currentUser.email);
      alert(`A password reset link was sent to ${currentUser.email}.`);
    } catch (err) {
      console.error(err);
      alert("Could not send a reset link. Please try again.");
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    const target = inviteEmail.trim();
    if (!target) {
      return;
    }
    setBusy(true);
    try {
      await inviteMember(target, effInviteTier);
      setInviteEmail("");
      await reloadGrants();
    } catch (err) {
      alert(inviteErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // Owner consumes one of their own seats for personal access. Updates auth
  // state so gating reflects access immediately, then sends them to play.
  const handleUseOwnSeat = async () => {
    setBusy(true);
    try {
      const { tier } = await claimOwnerSeat(effOwnSeatTier);
      if (authUserData) {
        setAuthUserData({ ...authUserData, tier, hasAccess: true });
      }
      navigate("/activity");
    } catch (err) {
      alert(inviteErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleSendEmail = async (grant) => {
    setBusy(true);
    try {
      await sendInviteEmail(grant.email);
      alert(`Invite email sent to ${grant.email}.`);
    } catch (err) {
      console.error(err);
      alert("Could not send the invite email. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  // Reassign moves the (unclaimed) seat to a new email. The server does the
  // delete + re-invite in one transaction, so the seat is never left empty if
  // the new email is invalid/taken. Only the grant's email (the doc id)
  // changes; the seat keeps its tier.
  const handleReassign = async (grant) => {
    const next = window.prompt(
      `Reassign this seat from ${grant.email} to which email?`,
    );
    if (!next || !next.trim()) {
      return;
    }
    setBusy(true);
    try {
      await reassignMember(grant.email, next.trim());
      await reloadGrants();
    } catch (err) {
      // The transaction is all-or-nothing, so the original seat is intact on
      // failure; refresh anyway and surface why.
      await reloadGrants();
      alert(inviteErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (grant) => {
    if (
      !window.confirm(
        `Revoke this seat? ${grant.email} will lose access${
          grant.memberUid ? "" : " (they have not signed in yet)"
        }.`,
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await revokeMember(grant.email);
      await reloadGrants();
    } catch (err) {
      console.error(err);
      alert("Could not revoke the seat. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="accountHeader">
        <h1 style={{ fontFamily: "NexaBold", fontSize: "22px" }}>My Account</h1>
        <div
          onClick={async () => {
            await signOut(auth);
            navigate("/login");
          }}
          id="backButtonContainer"
        >
          <BackIcon size={20} />
          <p style={{ paddingTop: "14px", paddingLeft: "5px" }}>Sign Out</p>
        </div>
      </div>

      {/* ---- Profile edit form ---- */}
      <form id="providerAccountForm">
        <p style={{ fontSize: "18px", fontWeight: "600" }}>
          Account Information
        </p>
        <div className="accountFormRowContainer">
          <div className="accountInputContainer">
            <label htmlFor="firstName">First Name</label>
            <input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              type="text"
              name="firstName"
            />
          </div>
          <div className="accountInputContainer">
            <label htmlFor="lastName">Last Name</label>
            <input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              type="text"
              name="lastName"
            />
          </div>
        </div>
        <div className="accountFormRowContainer">
          <div className="accountInputContainer">
            <label htmlFor="email">Email Adress</label>
            <input
              style={{ width: "600px" }}
              disabled={true}
              value={email}
              type="text"
              name="email"
            />
          </div>
        </div>
        <div className="accountFormRowContainer">
          <div className="accountInputContainer">
            <label htmlFor="nameOfPractice">Name Of Practice</label>
            <input
              style={{ width: "600px" }}
              value={practice}
              onChange={(e) => setPractice(e.target.value)}
              type="text"
              name="nameOfPractice"
            />
          </div>
        </div>
        <div className="accountFormRowContainer">
          <div className="accountInputContainer">
            <label htmlFor="address">Practice Street Adress</label>
            <input
              style={{ width: "500px" }}
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              type="text"
              name="address"
            />
          </div>
          <div className="accountInputContainer">
            <label htmlFor="city/state/region/province">
              City / State / Region / Province
            </label>
            <input
              style={{ width: "250px" }}
              value={city}
              onChange={(e) => setCity(e.target.value)}
              type="text"
              name="city/state/region/province"
            />
          </div>
        </div>
        <div className="accountFormRowContainer">
          <div className="accountInputContainer">
            <label htmlFor="country">Country</label>
            <input
              style={{ width: "500px" }}
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              type="text"
              name="country"
            />
          </div>
          <div className="accountInputContainer">
            <label htmlFor="zipCode">Zip / Postal Code</label>
            <input
              style={{ width: "250px" }}
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
              type="text"
              name="zipCode"
            />
          </div>
        </div>
        <div
          className="editButtonContainer"
          style={{ display: "flex", gap: "1rem" }}
        >
          <button
            onClick={handleEditInformation}
            type="submit"
            className="editButton"
          >
            Edit Information
          </button>
          <button
            type="button"
            className="editButton"
            onClick={handleResetSelf}
          >
            Reset password
          </button>
        </div>
      </form>

      {/* ---- My Access (the caller's claimed seat) ---- */}
      <div className="licensesContainer">
        <p style={{ fontSize: "18px", fontWeight: "600" }}>My Access</p>
        {myAccessGrant ? (
          <>
            <div className="licenseTableScroll">
              <table className="licenseTable">
                <thead>
                  <tr>
                    <th>Tier</th>
                    <th>Renews / Expires</th>
                    <th>Provider Admin Email</th>
                  </tr>
                </thead>
                <tbody>
                  <MyAccessRow grant={myAccessGrant} />
                </tbody>
              </table>
            </div>
            <div style={{ paddingTop: "1rem" }}>
              <button
                className="editButton"
                onClick={() => navigate("/activity")}
              >
                Play
              </button>
            </div>
          </>
        ) : (
          <>
            <p>You don't have an active seat yet.</p>
            {ownerGrant ? (
              <>
                <p>
                  Use one of your own seats for yourself
                  {freeSeats > 0
                    ? tiersWithFree.length > 1
                      ? " — pick which tier to use:"
                      : `, at the ${capitalize(effOwnSeatTier)} tier.`
                    : " — all seats are currently assigned. Revoke one below to free a seat."}
                </p>
                {freeSeats > 0 && (
                  <div
                    style={{
                      display: "flex",
                      gap: "1rem",
                      alignItems: "center",
                      paddingTop: "1rem",
                      flexWrap: "wrap",
                    }}
                  >
                    {tiersWithFree.length > 1 ? (
                      <select
                        value={effOwnSeatTier}
                        onChange={(e) => setOwnSeatTier(e.target.value)}
                      >
                        {tiersWithFree.map((t) => (
                          <option key={t} value={t}>
                            {capitalize(t)} ({freeForTier(t)} free)
                          </option>
                        ))}
                      </select>
                    ) : (
                      <TierPill tier={effOwnSeatTier} />
                    )}
                    <button
                      className="editButton"
                      disabled={busy}
                      onClick={handleUseOwnSeat}
                    >
                      Use a seat for myself
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                <p>
                  Ask your practice to invite your email address, or buy a plan
                  of your own.
                </p>
                <div style={{ paddingTop: "1rem" }}>
                  <button
                    className="editButton"
                    onClick={() => navigate("/purchase")}
                  >
                    Buy a plan
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* ---- Seats I Manage (owner grant + assigned seats) ---- */}
      <div className="licensesContainer">
        <p style={{ fontSize: "18px", fontWeight: "600" }}>Seats I Manage</p>
        {!ownerGrant ? (
          <>
            <p>You don't manage any seats.</p>
            <div style={{ paddingTop: "1rem" }}>
              <button
                className="editButton"
                onClick={() => navigate("/purchase")}
              >
                Buy seats
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Per-tier seat usage. Each tier's capacity comes from the
                subscriptions backing it; a partial cancel can leave a tier
                "over capacity" until renewal or a manual revoke. */}
            <div style={{ paddingBottom: "0.5rem" }}>
              {displayTiers.map((t) => {
                const cap = seatsByTier[t] || 0;
                const used = usedForTier(t);
                return (
                  <p
                    key={t}
                    style={{
                      margin: 0,
                      display: "flex",
                      gap: "0.5rem",
                      alignItems: "center",
                    }}
                  >
                    <TierPill tier={t} />
                    <span style={{ color: used > cap ? "red" : "inherit" }}>
                      {used} / {cap} seats used
                      {used > cap ? " — over capacity" : ""}
                    </span>
                  </p>
                );
              })}
            </div>

            {/* Invite form — shown ONLY when some tier still has a free seat.
                The tier picker appears when more than one tier has capacity. */}
            {freeSeats > 0 ? (
              <form
                onSubmit={handleInvite}
                style={{
                  display: "flex",
                  gap: "1rem",
                  alignItems: "flex-end",
                  flexWrap: "wrap",
                  paddingBottom: "1rem",
                }}
              >
                <div className="accountInputContainer">
                  <label htmlFor="inviteEmail">Invite by email</label>
                  <input
                    id="inviteEmail"
                    style={{ width: "320px" }}
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="teammate@practice.com"
                  />
                </div>
                <div className="accountInputContainer">
                  <label htmlFor="inviteTier">Tier</label>
                  {/* Seat tier is fixed at invite time and never auto-upgrades.
                      Pick among the tiers that still have a free seat; with a
                      single tier it's shown as a pill (no choice to make). */}
                  {tiersWithFree.length > 1 ? (
                    <select
                      id="inviteTier"
                      value={effInviteTier}
                      onChange={(e) => setInviteTier(e.target.value)}
                    >
                      {tiersWithFree.map((t) => (
                        <option key={t} value={t}>
                          {capitalize(t)} ({freeForTier(t)} free)
                        </option>
                      ))}
                    </select>
                  ) : (
                    <TierPill tier={effInviteTier} />
                  )}
                </div>
                <button
                  type="submit"
                  className="editButton"
                  disabled={busy || inviteEmail.trim().length === 0}
                >
                  Invite
                </button>
              </form>
            ) : (
              <p style={{ paddingBottom: "1rem" }}>
                All seats are assigned.
                {assignedSeats.length > 0
                  ? " Revoke one below to free a seat."
                  : ""}
              </p>
            )}

            {assignedSeats.length === 0 ? (
              <p>You haven't assigned any seats yet.</p>
            ) : (
              <div className="licenseTableScroll">
                <table className="licenseTable">
                  <thead>
                    <tr>
                      <th>Assigned Email</th>
                      <th>Status</th>
                      <th>Tier</th>
                      <th>Renews / Expires</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assignedSeats
                      .slice()
                      .sort((a, b) => a.email.localeCompare(b.email))
                      .map((grant) => (
                        <SeatRow
                          key={grant.id}
                          grant={grant}
                          busy={busy}
                          onSendEmail={handleSendEmail}
                          onReassign={handleReassign}
                          onRevoke={handleRevoke}
                        />
                      ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ display: "flex", gap: "2rem", paddingTop: "1rem" }}>
              <button
                className="editButton"
                onClick={() => navigate("/purchase")}
              >
                Buy more seats
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// Map an inviteMember/reassign HttpsError to a user-facing message.
function inviteErrorMessage(err) {
  switch (err?.code) {
    case "functions/resource-exhausted":
      // Server message names the exhausted tier (e.g. "All parents seats are
      // assigned."); fall back to a generic line if absent.
      return (
        err.message ||
        "All your seats are assigned. Revoke one or buy more seats."
      );
    case "functions/already-exists":
      // The server distinguishes "already redeemed" from "pending invite" —
      // surface its specific message.
      return err.message || "That email already has a seat.";
    case "functions/failed-precondition":
      return "Your subscription isn't active, so seats can't be assigned.";
    case "functions/invalid-argument":
      return "Please enter a valid email address.";
    default:
      return "Something went wrong. Please try again.";
  }
}
